<script setup lang="ts">
import { computed, onMounted, onBeforeMount, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useRoute } from 'vue-router';
import { AxiosResponse } from 'axios';
import AppTabMenu from '@/components/AppTabMenu';
import ArchiveModal from '@/components/Modal/ArchiveModal/index';
import { ImpersonateButton, ResetPasswordButton, ActivateUserButton } from '@/components/Buttons';
import { UserHeaderTotals } from '.';
import { PersonStores, useArchiveStore, useAuthStore } from '@/store';
import { useRouting, can, useTenant } from '@/composables';
import { PERMISSIONS, TENANT_SETTINGS } from '@/enum';
import { UserReferrals, UserList, UsersToBeArchived } from '..';

const toast = useToast();

const $routing = useRouting();

const $tenant = useTenant();

const profileStore = PersonStores.useUserProfileStore();
const archiveStore = useArchiveStore();
const authStore = useAuthStore();
const route = useRoute();
const userRoutes = ref([
  {
    route: `/users`,
    label: 'All Users',
    component: UserList,
    visible: true,
  },
  {
    route: `/users/referrals`,
    label: 'Unassigned Students',
    component: UserReferrals,
    visible: can([PERMISSIONS.REFERRAL_MANAGE]) && $tenant.tenantSettingEnabled(TENANT_SETTINGS.REFERRALS),
  },
  {
    route: `/users/to-be-archived`,
    label: 'SIS To Be Archived',
    component: UsersToBeArchived,
    visible:
      can([PERMISSIONS.PEOPLE_TO_BE_ARCHIVED]) &&
      $tenant.tenantSettingEnabled(TENANT_SETTINGS.USE_PEOPLE_TO_BE_ARCHIVED),
  },
]);

onBeforeMount(async () => {
  if (route.params.id && !profileStore.id) await profileStore.setUser(Number(route.params.id));

  profileStore.currentRoute = $routing.route.name;
});

/**
 * The default header title text.
 */
const headerTitleDefault = 'Users';

/**
 * The header title reference value.
 */
const headerTitle = ref(headerTitleDefault);

/**
 * Whether or not the display the skeleton loader.
 */
const showLoader = ref(false);

/**
 * Initialize everything required to build out the component.
 */
onMounted(() => defineHeaderTitle());

/**
 * Watch for a change within the route in order to change the header title.
 */
$routing.routeChangeWatcher(() => {
  profileStore.currentRoute = $routing.route.name;
  defineHeaderTitle();
});

/**
 * Watch for a change within the user profile store in order to change the header title.
 */
watch(profileStore, () => {
  defineHeaderTitle();
});

const openArchiveModal = () => {
  archiveStore.person = {
    id: profileStore.person?.id as number,
    display_name: profileStore.person?.display_name as string,
    archived_at: profileStore.person?.archived_at as string,
    people_type: {
      acronym: profileStore.person?.people_type?.acronym as string,
    },
  };
  archiveStore.isShow = true;
};
/**
 * Define the header title text.
 */
const defineHeaderTitle = () => {
  if (isUserList.value) {
    headerTitle.value = headerTitleDefault;
  } else {
    showLoader.value = true;
    if (!profileStore.loading && profileStore.person?.display_name) {
      headerTitle.value = profileStore.person?.display_name;
      showLoader.value = false;
    }
  }
};

/**
 * Define a reusable value to check if the current route is the user listing.
 */
const isUserList = computed(
  () =>
    $routing.route.name === 'user.list' ||
    $routing.route.name === 'user.referrals' ||
    $routing.route.name === 'user.to-be-archived',
);
const activeIndex = computed(() => ($routing.route.name === 'user.list' ? 0 : 1));
/**
 * Only show the back button if the current view is that of the user listing.
 */
const showBackButton = computed(() => !isUserList.value);

const doToast = (response: AxiosResponse) => {
  const success = response.status === 200;
  toast.add({
    severity: success ? 'success' : 'error',
    summary: success ? 'Unarchived' : 'Error',
    detail: success ? 'User Unarchived.' : response.statusText,
    life: 3000,
  });
};

const updateProfile = () => {
  profileStore.setUser(profileStore.person?.id as number);
};

const unarchivePerson = async () => {
  const response = await archiveStore.unarchive(profileStore.person?.id as number);
  if (response.status === 200) {
    updateProfile();
  }
  doToast(response);
};

const isFromAdminRoute = computed<boolean>(() => $routing.route.name === 'administrators.userProfile');

/**
 * make sure the person editing cannot impersonate or change pwrd
 */
const isNotMe = computed(() => profileStore.id !== authStore.user?.id);
</script>

<template>
  <div class="page-header page-header-with-tabs w-full">
    <div class="flex justify-content-between align-items-center w-full h-2rem">
      <div>
        <RouterLink v-if="!isFromAdminRoute && showBackButton" to="/users" class="no-underline">
          <Icon name="arrow_back" class="text-xs" />
          <div class="link-margin text-xs">All Users</div>
        </RouterLink>
        <RouterLink v-if="isFromAdminRoute && showBackButton" to="/admin/administrators" class="no-underline">
          <Icon name="arrow_back" class="text-xs" />
          <div class="link-margin text-xs">All Administrators</div>
        </RouterLink>
        <Skeleton v-if="showLoader" width="20rem" height="2.3rem" />
        <h1 v-else v-text="headerTitle" />
      </div>
      <UserHeaderTotals v-if="!isFromAdminRoute" />
    </div>
    <div v-if="showBackButton" class="page-header-tab-bar">
      <AppTabMenu v-if="profileStore.selectedUserRoutes" :items="profileStore.selectedUserRoutes" />
      <div v-if="!profileStore.loading && isNotMe" class="page-header-tab-bar-right">
        <ArchiveModal v-if="profileStore.person" @on-archived="updateProfile" />
        <AppButton
          v-if="can([PERMISSIONS.PEOPLE_ARCHIVE]) && !profileStore.person?.archived_at"
          type="secondary"
          text="Archive"
          icon-left="archive"
          @click="openArchiveModal"
        />
        <AppButton
          v-if="can([PERMISSIONS.PEOPLE_UNARCHIVE]) && profileStore.person?.archived_at"
          type="secondary"
          text="Unarchive"
          icon-left="unarchive"
          @click="unarchivePerson"
        />
        <ResetPasswordButton
          v-if="
            can([PERMISSIONS.AUTH_PASSWORD_RESET_USER]) &&
            !profileStore.person?.archived_at &&
            profileStore.person?.user
          "
        />
        <ImpersonateButton v-if="can([PERMISSIONS.IMPERSONATE]) && !profileStore.person?.archived_at" />
        <ActivateUserButton v-if="can([PERMISSIONS.PEOPLE_SEND_ACTIVATE_LINK]) && !profileStore.person?.archived_at" />
      </div>
    </div>
    <div
      v-else-if="
        $tenant.tenantSettingEnabled(TENANT_SETTINGS.REFERRALS) &&
        userRoutes.filter((route) => route.visible).length > 1
      "
    >
      <AppTabMenu :items="userRoutes" :active-index="activeIndex" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.link-margin {
  margin: -16px 0 0 14px;
}
</style>
